import { useInfiniteQuery } from '@tanstack/react-query';

import { formatUsers } from 'hb-react/frontoffice/components/NotifyPeopleModal/utils';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import type { User } from 'hb-react/types';

import type { FormValues } from '../types';

export const MAX_SELECTED_USERS_DISPLAYED = 100;

interface UsersTargetingResponse {
  users: User[];
  total_items: number;
  user_ids: number[];
}

export const useInfiniteUsersTargeting = ({
  perPage = MAX_SELECTED_USERS_DISPLAYED,
  query,
}: {
  perPage?: number;
  query: FormValues['query'];
}) => {
  const { topicId } = useTopicContext();

  return useInfiniteQuery({
    queryKey: ['users-list', topicId, JSON.stringify(query)],
    queryFn: async ({ pageParam }) => {
      const path = topicId
        ? Routes.private_frontoffice_users_topics_notify_people_recommendations_path(
            topicId,
          )
        : Routes.private_frontoffice_users_notify_people_recommendations_path();

      const newQuery = topicId ? { ...query, topic_ids: [topicId] } : query;

      try {
        const { data } = await axios.get<UsersTargetingResponse>(path, {
          params: {
            page: pageParam,
            query: newQuery,
            per_page: perPage,
            query_options: { user_ids: true },
          },
        });

        return {
          users: formatUsers(data?.users),
          count: data?.total_items,
          userIds: data?.user_ids,
          pageParam,
        };
      } catch (error) {
        throw new Error(error);
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.pageParam + 1,
    retry: false,
    refetchOnMount: false,
  });
};
